import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'hooks/store';
import {
  cleanOrdersAction,
  getOrderListAction,
  isLoadingOrdersSelector,
  ordersEntitiesSelector,
  ordersIdsSelector,
  remoteTotalSelector,
} from 'state/slices/order';
import { usePagination } from 'hooks/pagination';
import {
  Client,
  ClientLocation,
  Order,
  Organization,
  PaginationData,
} from 'constants/interface';
import {
  Switch,
  Table,
  Tag,
} from 'UI';
import {
  OrderApiStatus,
  OrderOtherStatusEnum,
  OrderProductionStatusEnum,
  OrderProposalStatusEnum,
  OrderStatus,
  OrderStatusDisplayMapping,
  OrderTypeDisplayMapping,
  OrderTypeEnum,
  OrderViewStatusEnum,
} from 'constants/order';

import TableRowAction from './components/TableRowAction';

import {
  OrserSearchInput,
  SearchContainer,
  SwitchContainer,
} from './style';

interface OrdersTabProps {
  onOrderCopy: (orderId: string) => void
  selectedOrg: Organization
}

const OrdersTab = ({
  onOrderCopy,
  selectedOrg,
}: OrdersTabProps): JSX.Element => {
  const dispatch = useDispatch();
  const remoteTotal = useSelector(remoteTotalSelector);

  const ordersEntities = useSelector(ordersEntitiesSelector);
  const ordersIds = useSelector(ordersIdsSelector);
  const isRemoteLoading = useSelector(isLoadingOrdersSelector);

  const enableCancelDisplayEffect = useRef(false);
  const [shouldDisplayCancelled, setDisplayCancelled] = useState(false);

  const orders = ordersIds.map((orderId) => {
    const entity = ordersEntities[orderId];
    if (!entity) return undefined;
    const tableOrder: Order & {
      key?: string,
    } = {
      ...entity,
      key: entity.id,
    };
    return tableOrder;
  }).filter((tableOrder) => !!tableOrder) as (Order & { key: string})[];

  const [currentActiveAction, setCurrentActiveAction] = useState<number>();

  const remoteFetch = (
    paginationData: PaginationData,
  ) => {
    const status = shouldDisplayCancelled
      ? OrderOtherStatusEnum.CANCELED
      : paginationData.filter?.status as OrderApiStatus // TODO: use generics for filter type check
      || OrderViewStatusEnum.NON_CANCELED;
    dispatch(getOrderListAction({
      ...paginationData,
      status,
    }));
  };
  const pagination = usePagination<Order>(
    remoteFetch,
    {
      filter: {
        status: undefined,
        type: undefined,
        organization: selectedOrg.id,
        startDate: undefined,
        endDate: undefined,
      },
      order: 'descend',
    },
  );

  useEffect(() => {
    dispatch(cleanOrdersAction());
  }, []);

  useEffect(() => {
    pagination.updateTotal(remoteTotal);
  }, [remoteTotal]);

  const sorter = (): number => 0;

  const pageData = pagination.getInternal();

  const orderTypeFilter = pageData.filter.type ?? null;
  const orderStatusFilter = pageData.filter.status ?? null;

  const statusFilterList = [
    ...Object.values(OrderProposalStatusEnum),
    ...Object.values(OrderProductionStatusEnum),
    ...Object.values(OrderOtherStatusEnum),
  ];

  const onCancelledFilterChanged = (isChecked: boolean) => {
    setDisplayCancelled(isChecked);
  };

  useEffect(() => {
    if (enableCancelDisplayEffect.current) {
      pagination.fetch();
    } else {
      enableCancelDisplayEffect.current = true;
    }
  }, [enableCancelDisplayEffect, shouldDisplayCancelled]);

  return (
    <>
      <SearchContainer>
        <OrserSearchInput placeholder="Buscar pedidos" onSearch={pagination.onSearch} allowClear />
        <SwitchContainer>
          <span>Exibir cancelados:</span>
          <Switch title="Exibir cancelados" onChange={onCancelledFilterChanged} />
        </SwitchContainer>
      </SearchContainer>
      <Table<Order>
        dataSource={orders}
        pagination={pageData}
        loading={isRemoteLoading}
        showSorterTooltip={false}
        locale={{
          filterReset: 'Limpar',
          cancelSort: 'Clique para limpar ordenação',
          triggerAsc: 'Clique para ordem crescente',
          triggerDesc: 'Clique para ordem decrescente',
        }}
        onChange={pagination.onTableChange}
      >
        <Table.Column
          title="OS​"
          key="seq"
          dataIndex="seq"
          sorter={sorter}
          sortOrder={
            pageData.orderBy === 'seq'
              ? pageData.order
              : undefined
          }
        />
        <Table.Column
          title="Cliente"
          key="client"
          dataIndex="client"
          render={(
            client: Client,
          ) => client.name}
        />
        <Table.Column
          title="Localização"
          key="location"
          dataIndex="location"
          render={(
            location: ClientLocation,
            order: Order,
          ) => {
            const display = location
              ? `${location.name} - ${location.city}/${location.state} (${location.maintainer})`
              : `${order.client.city}/${order.client.state}`;
            return display;
          }}
        />
        <Table.Column
          title="Formação​"
          key="type"
          dataIndex="type"
          filters={Object.values(OrderTypeEnum).map((orderType) => ({
            text: OrderTypeDisplayMapping[orderType].name,
            value: orderType,
          }))}
          filterMultiple={false}
          filteredValue={orderTypeFilter !== null ? [orderTypeFilter] : null}
          render={(type: OrderTypeEnum) => {
            const displayName = OrderTypeDisplayMapping[type].name;
            return displayName;
          }}
        />
        <Table.Column
          title="Data do pedido"
          key="createdAt"
          dataIndex="createdAt"
          render={(createdAt: string) => {
            const date = new Date(createdAt);
            const formattedDate = date.toLocaleDateString('pt-br');
            return formattedDate;
          }}
          sorter={sorter}
          sortOrder={
            pageData.orderBy === 'createdAt'
              ? pageData.order
              : undefined
          }
        />
        <Table.Column
          title="Status"
          key="status"
          dataIndex="status"
          filters={statusFilterList.map((statusType) => ({
            text: OrderStatusDisplayMapping[statusType].name,
            value: statusType,
          }))}
          filterMultiple={false}
          filteredValue={orderStatusFilter !== null ? [orderStatusFilter] : null}
          render={(status: OrderStatus) => {
            const displayName = OrderStatusDisplayMapping[status].name;
            return (
              <Tag color={OrderStatusDisplayMapping[status].color}>
                {displayName}
              </Tag>
            );
          }}
        />
        <Table.Column
          title=""
          key="action"
          dataIndex="id"
          render={(id: string, record: Order, index: number) => {
            const actionButton = (
              <TableRowAction
                order={record}
                index={index}
                isActive={currentActiveAction === index}
                onOrderCopy={onOrderCopy}
                onVisibleChange={(visibleIndex) => setCurrentActiveAction(visibleIndex)}
              />
            );
            return actionButton;
          }}
        />
      </Table>
    </>
  );
};

export default OrdersTab;
